import * as React from "react";
import {
  alpha,
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Grow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import pallets from "../../../assets/pallets.jpg";
import bananas from "../../../assets/bananas.jpg";

export default function Hero() {
  const { t } = useTranslation();
  const [currentBackground, setCurrentBackground] = React.useState(pallets);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBackground((prev) => (prev === pallets ? bananas : pallets));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Box
      id="hero"
      sx={{
        width: "100%",
        minHeight: { xs: "75vh", md: "95vh" },
        backgroundImage: `url(${currentBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        pt: "4rem",
        transition: "background-image 1s ease-in-out",
      }}
    >
      <Container
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Grow in={isVisible} timeout={800}>
          <Box
            sx={(theme) => ({
              backgroundColor: "rgba(42,63,63,0.88)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              borderRadius: "2rem",
              outline: "1px solid",
              width: { xs: "98%", md: "1000px", xl: "1400px" },
              minHeight: { xs: "480px", md: "520px" },
              outlineColor:
                theme.palette.mode === "light"
                  ? alpha("#BFCCD9", 0.5)
                  : alpha("#445f64", 0.1),
              boxShadow:
                theme.palette.mode === "light"
                  ? `0 0 12px 8px ${alpha("#445f64", 0.2)}`
                  : `0 0 24px 12px ${alpha("rgba(26,73,73,0.99)", 0.2)}`,
              p: { xs: 3, md: 6 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              marginY: 4,
              position: "relative",
              overflow: "hidden",
            })}
          >
            {/* Subtle shimmer effect */}
            <Box
              sx={{
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background:
                  "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.03) 50%, transparent 70%)",
                animation: "shimmer 4s infinite",
                "@keyframes shimmer": {
                  "0%": {
                    transform:
                      "translateX(-100%) translateY(-100%) rotate(45deg)",
                  },
                  "100%": {
                    transform:
                      "translateX(100%) translateY(100%) rotate(45deg)",
                  },
                },
              }}
            />

            <Grow in={isVisible} timeout={1000}>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  letterSpacing: 2,
                  mb: 1,
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  fontWeight: 500,
                }}
              >
                {t("startpage.overline", "För grossister och importörer")}
              </Typography>
            </Grow>

            <Grow in={isVisible} timeout={1200}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.4rem", md: "4rem" },
                  fontWeight: 700,
                  color: "white",
                  mb: 2,
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                }}
              >
                {t("startpage.heroheadline")}
              </Typography>
            </Grow>

            <Grow in={isVisible} timeout={1400}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1.4rem", md: "2rem" },
                  color: "rgba(255,255,255,0.9)",
                  mb: 4,
                  maxWidth: "800px",
                  lineHeight: 1.5,
                }}
              >
                {t("startpage.herosub")}
              </Typography>
            </Grow>

            <Stack
              direction={"column"}
              spacing={4}
              alignItems="center"
              justifyContent="center"
              sx={{ width: "100%" }}
            >
              {/* Hero list */}
              <Stack spacing={2} textAlign="left">
                {[
                  t("startpage.herolist1"),
                  t("startpage.herolist2"),
                  t("startpage.herolist3"),
                ].map((text, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      opacity: 0,
                      animation: "slideIn 0.5s ease-out forwards",
                      animationDelay: "1.6s",
                      "@keyframes slideIn": {
                        from: {
                          opacity: 0,
                          transform: "translateX(-15px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateX(0)",
                        },
                      },
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        color: "#66BB6A",
                        fontSize: 22,
                        filter: "drop-shadow(0 0 4px rgba(102,187,106,0.4))",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "white",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Hero button */}
              <Box
                sx={{
                  opacity: 0,
                  animation: "slideIn 0.5s ease-out forwards",
                  animationDelay: "1.6s",
                  "@keyframes slideIn": {
                    from: {
                      opacity: 0,
                      transform: "translateX(-15px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  href="https://calendly.com/partihandel"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon sx={{ ml: 0.5 }} />}
                  sx={{
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: 600,
                    py: { xs: 1.75, md: 2 },
                    px: { xs: 3, md: 4 },
                    borderRadius: "50px",
                    whiteSpace: "nowrap",
                    minWidth: { xs: "220px", md: "260px" },
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)",
                    boxShadow: "0 8px 20px rgba(76,175,80,0.25)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
                      transition: "left 0.6s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 28px rgba(76,175,80,0.35)",
                      background:
                        "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                  }}
                >
                  {t("startpage.herobutton")}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Grow>
      </Container>
    </Box>
  );
}
